import { Fragment, useRef, useEffect, useState, useMemo, useCallback } from 'react'
import Image from 'next/image'
import { styled } from '@mui/material'

import { useTranslation } from 'src/utils/i18n'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { setBlock, androidLocalRefresh } from 'src/@api/android'
import { Pagination, ConfigProvider } from 'antd'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import ContentLoading from 'src/components/ContentLoading'

// 自定义隐藏滚动条且保留滚动特性(div)
const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

// 自定义滚动条(ul)
const ScrollUlShow = styled('ul')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const BlockApplications = () => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const [useApp, setApp] = useState([]) // 获取到的数据
  const [showApp, setShowApp] = useState([]) // 搜索展示数据
  const [blockedBundleIds, setBlockedBundleIds] = useState([])
  const { context, data: appRecords, loading: isLoading } = useAndroidDevicePagedData({
    source: 'local',
    category: 'INSTALLED_APP',
    limit: 200
  })

  // 设备切换时重置应用屏蔽列表与搜索状态
  useDeviceScopedInit(
    useCallback(async (_, { context: scopedContext, isCurrent }) => {
      setApp([])
      setShowApp([])
      setBlockedBundleIds([])
      setCurrentPage(1)
      const result = await setBlock({ device_id: scopedContext?.username || '' })
      if (isCurrent()) setBlockedBundleIds(result?.data?.nameApp || [])
    }, [])
  )

  useEffect(() => {
    const apps = appRecords.map(item => ({ ...item, active: blockedBundleIds.includes(item.bundleid) }))
    setApp(apps)
    setShowApp(apps)
  }, [appRecords, blockedBundleIds])

  const isBlock = async ({ name, action }) => {
    const deviceId = context?.username || ''
    const result = await setBlock({ device_id: deviceId, type: 'nameApp', name, action })
    await androidLocalRefresh({ device_id: deviceId, type: 50, limit: action, bundleid: name })
    setBlockedBundleIds(result?.data?.nameApp || [])
  }

  const handleFilter = (e) => {
    const value = e.target.value.toLowerCase();
    const results = useApp.filter(item => { return item.name.toLowerCase().includes(value) })
    setShowApp(results)
    setCurrentPage(1)
  }

  const [currentPage, setCurrentPage] = useState(1)
  const total = showApp.length
  const currentItems = useMemo(() => {
    return showApp.slice((currentPage - 1) * 9, currentPage * 9)
  }, [showApp, currentPage])

  // 处理页面改变的事件
  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleImageError = event => {
    event.target.src = '/images/android/com.example.variousdata.png'
  }

  useEffect(() => {
    const title = t('Block Apps')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 埋点上报
  useEffect(() => {
    if (useApp.length === 0) return
    const events = useApp
      .filter(item => item?.active === true)
      .map(item => ({
        event_name: 'Click_Block',
        properties: {
          app: item?.name || '',
        }
      }))
    if (events.length > 0) {
    }
  }, [useApp])

  return (
    <Fragment>
      {/* 展示页 */}
      <ScrollDiv className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] bg-[#FFFFFF] relative'>
        {/* 应用列表 */}
        <ScrollUlShow className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] text-[#000000] font-[Poppins] p-[0px_30px_0px_66px] smj:p-[0px_0px_0px_21px]'>
          <li className='w-full flex justify-between items-center text-[16px] font-[600] leading-[24px] pt-[30px] pr-[79px] sticky top-0 smj:pt-[11px] smj:pr-[15px] bg-[#ffffff]'>
            <p className='smj:text-[18px] smj:font-medium'>{t('All apps')}</p>
            <div className='w-[227px] smj:w-[180px] mb-[18px] flex flex-col pt-[15px] pl-[15px]'>
              <TextField
                fullWidth
                className='h-[40px] bg-[rgba(235,235,239,0.92)] rounded-lg !border-0 text-[16px]'
                size='small'
                onInput={e => {
                  handleFilter(e)
                }}
                placeholder={t('Search')}
                sx={{
                  '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': { border: 0 },
                  '& input::placeholder': { fontSize: '16px', color: '#818187', opacity: 100 }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end' className=' cursor-pointer select-none'>
                      <Image src='/images/contacts/search.svg' width={24} height={24} alt='search'></Image>
                    </InputAdornment>
                  )
                }}
              />
            </div>
          </li>
          <div className='h-[calc(100vh-384px)] smj:h-[calc(100vh-240px)] overflow-auto'>

            {currentItems.map((item, index) => (
              <li key={index} className='w-full flex justify-between items-center p-[18px_79px_18px_0px] smj:p-[14px_15px_14px_0px] [border-bottom:1px_solid_#DCDCDC] last:border-none'>
                <div className='flex items-center'>
                  <Image src={'https://cos.spyx.com/' + item.iconUrl} alt='icon' width={36} height={36} className='w-[36px] h-[36px] mr-[24px] smj:mr-[15px]' onError={handleImageError} />
                  <p className='text-[16px] text-[#222222] font-[500] leading-[24px]'>{item.name}</p>
                </div>
                <div className='flex justify-end select-none'>
                  <button
                    className={`w-max text-[12px] text-[#000000] font-[Poppins] font-[400] leading-[18px] p-[6px_23px]  ${!item.active ? 'bg-[#FFCF55]' : 'bg-[#E5E6EA]'} rounded-[15px]`}
                    onClick={() => {
                      isBlock({ name: item.bundleid, action: !item.active })
                    }}
                  >{!item.active ? t('Block') : t('Unblock')}</button>
                </div>
              </li>
            ))}
          </div>

          {/* 分页器 */}
          <nav aria-label="Page navigation"
            style={{ overflow: 'hidden' }}
            className='w-[1200px] Headers:Bxl:w-[960px] Headers:Bxl:smj:w-[720px] Headers:Bxl:smj:Bld:Mlg:w-[540px] Headers:Bxl:smj:Bld:Sixmd:w-full m-auto flex flex-col items-center justify-center pb-[20px]'
          >
            <div className='flex flex-wrap justify-center '>
              <div className='flex justify-center items-center fenye pt-4'>
                <ConfigProvider
                  theme={{
                    components: {
                      Pagination: {
                        itemActiveBg: '', // 选中分页项的背景色
                        colorPrimary: '#666CFF',
                        colorPrimaryHover: '#666CFF',
                        colorText: '#222222',
                        fontSize: '14px',  // 调整字体大小
                        itemSize: '30px',  // 调整每个分页项的大小
                        itemMargin: '8px',  // 调整分页项之间的间距
                        lineHeight: '30px',  // 设置行高，确保字体垂直居中
                      }
                    }
                  }}
                >
                  <Pagination
                    total={total}
                    showSizeChanger={false}
                    pageSize={9}
                    current={currentPage}
                    showLessItems={true}
                    onChange={handlePageChange}
                    className='text-[14px]'
                    itemRender={(current, type, originalElement) => {
                      if (type === 'prev') {
                        return <div className='text-[#00000040] hover:text-[#12D8AB]'>&lt;</div> // 自定义左右箭头
                      }
                      if (type === 'next') {
                        return <div className='text-[#00000040] hover:text-[#12D8AB]'>&gt;</div> // 自定义右箭头
                      }

                      return originalElement
                    }}
                  ></Pagination>
                </ConfigProvider>
              </div>
            </div>
          </nav>
        </ScrollUlShow>
        {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(useApp.length > 0 || isLoading) ||
          <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </ScrollDiv>
    </Fragment >
  )
}

export default BlockApplications


